import { useRef, useState } from "react";
import Avatar from "@/components/dashboard/chat/Avatar";

/** Chats with unread messages, pinned to the bottom of the screen.
 *  Desktop: Messenger-style tabs (avatar + name + flashing count) bottom-right.
 *  Phone: stacked chat-head bubbles with a flashing count. Tapping opens that
 *  conversation in the chat dock; swiping a head sideways dismisses it until
 *  the next message arrives in that chat. Themed via #chat-heads. */
const DISMISS_KEY = "em_chat_heads_dismissed"; // { [conversation_id]: last_message_at }
const SWIPE_PX = 64;

const readDismissed = () => {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}") || {}; } catch { return {}; }
};

export default function ChatHeads({ items, onOpen, chatOpen, chatMin }) {
  const [dismissed, setDismissed] = useState(readDismissed);
  const [drag, setDrag] = useState(null); // { id, dx, leaving }
  const start = useRef(null); // { id, x, moved }

  if (!items?.length || (chatOpen && !chatMin)) return null;
  // A dismissed head stays hidden while the chat's latest message is still the
  // one it was dismissed on; a newer message brings it back.
  const visible = items.filter((c) => dismissed[c.conversation_id] !== (c.last_message_at || ""));
  if (!visible.length) return null;

  const dismiss = (c) => {
    const next = { ...dismissed, [c.conversation_id]: c.last_message_at || "" };
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const onPointerDown = (e, c) => {
    start.current = { id: c.conversation_id, x: e.clientX, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const s = start.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) > 8) s.moved = true;
    if (s.moved) setDrag({ id: s.id, dx });
  };
  const onPointerUp = (e, c) => {
    const s = start.current;
    start.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (s.moved && Math.abs(dx) >= SWIPE_PX) {
      setDrag({ id: c.conversation_id, dx: Math.sign(dx) * 400, leaving: true });
      setTimeout(() => { dismiss(c); setDrag(null); }, 180);
      return;
    }
    setDrag(null);
    if (!s.moved) onOpen(c.conversation_id);
  };

  const count = (n) => (n > 99 ? "99+" : n);
  const phone = typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches;
  return (
    <div
      id="chat-heads"
      data-chat-min={chatMin ? "1" : "0"}
      className="pointer-events-none fixed bottom-4 right-3 z-[66] flex flex-col items-end gap-2 sm:right-4 sm:flex-row sm:items-end"
      data-testid="chat-heads"
    >
      {visible.slice(0, 6).map((c) => {
        const d = drag?.id === c.conversation_id ? drag : null;
        const style = d
          ? { transform: `translateX(${d.dx}px)`, opacity: Math.max(0, 1 - Math.abs(d.dx) / 260), transition: d.leaving ? "transform 0.18s ease-out, opacity 0.18s ease-out" : "none", touchAction: "pan-y" }
          : { touchAction: "pan-y" };
        return (
          <button
            key={c.conversation_id}
            type="button"
            onPointerDown={(e) => onPointerDown(e, c)}
            onPointerMove={onPointerMove}
            onPointerUp={(e) => onPointerUp(e, c)}
            onPointerCancel={() => { start.current = null; setDrag(null); }}
            onDragStart={(e) => e.preventDefault()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(c.conversation_id); } if (e.key === "Delete" || e.key === "Backspace") dismiss(c); }}
            style={style}
            aria-label={`${c.unread_count} unread in ${c.name}. Swipe to dismiss.`}
            title={c.last_message_preview ? `${c.last_sender_name ? `${c.last_sender_name}: ` : ""}${c.last_message_preview}` : c.name}
            data-testid={`chat-head-${c.conversation_id}`}
            className="chat-head pointer-events-auto relative flex select-none items-center rounded-full border border-white/10 bg-[#0d0d0d] text-white shadow-[0_12px_32px_rgba(0,0,0,0.45)] transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1a8cff] sm:gap-2 sm:py-1.5 sm:pl-1.5 sm:pr-3"
          >
            <span className="relative flex h-12 w-12 items-center justify-center sm:h-8 sm:w-8">
              <Avatar name={c.name} picture={c.picture} size={phone ? 48 : 32} showStatus={false} />
            </span>
            <span className="hidden max-w-[140px] truncate text-xs font-semibold sm:block" data-testid={`chat-head-name-${c.conversation_id}`}>
              {c.name}
            </span>
            <span
              className="chat-head-badge absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#1a8cff] px-1.5 text-[10px] font-bold leading-none text-white ring-2 ring-[#0d0d0d]"
              data-testid={`chat-head-count-${c.conversation_id}`}
            >
              {count(c.unread_count)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
